/**
 * Viral Metrics Queries
 *
 * Story 6.5: Viral Tracking and Attribution
 *
 * Provides queries for calculating viral coefficient (K-factor)
 * and other viral growth metrics.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types';

type DbClient = SupabaseClient<Database>;

/**
 * Viral metrics calculated for a given period
 */
export interface ViralMetrics {
  /** K-factor: (referral conversions / deployed bots) */
  kFactor: number;
  /** Average number of referrals per bot that generated any */
  avgReferralsPerBot: number;
  /** Conversion rate: referral visits that became bots */
  conversionRate: number;
  /** Total bots created via referral */
  totalReferrals: number;
  /** Total bots deployed in the period */
  totalDeployed: number;
}

/**
 * Weekly K-factor data point
 */
export interface WeeklyKFactor {
  /** ISO week string (YYYY-WW) */
  week: string;
  /** K-factor for that week */
  kFactor: number;
  /** Number of referral conversions */
  referralConversions: number;
  /** Number of deployed bots */
  deployedBots: number;
}

/**
 * Top referring bot data
 */
export interface TopReferringBot {
  botId: string;
  name: string;
  slug: string;
  referralCount: number;
  conversionCount: number;
}

/**
 * Referral funnel data
 */
export interface ReferralFunnel {
  /** Landing page visits with referral */
  visits: number;
  /** Bot creation started with referral */
  starts: number;
  /** Bot creation completed with referral */
  completions: number;
  /** Signups with referral */
  signups: number;
}

/**
 * Date range filter
 */
export interface DateRange {
  start: Date;
  end: Date;
}

/**
 * Get default 30-day rolling window
 */
function getDefault30DayRange(): DateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { start, end };
}

/**
 * Calculate viral coefficient (K-factor) for a given period
 *
 * K = (Bots created with referral) / (Total deployed bots in period)
 *
 * A K > 1 means viral growth (each bot creates more than one new bot)
 * Target: K >= 0.05 (5%) by Month 3
 */
export async function calculateViralCoefficient(
  client: DbClient,
  dateRange: DateRange = getDefault30DayRange()
): Promise<ViralMetrics> {
  const { start, end } = dateRange;

  // Count bots with referrer_bot_id (referral conversions) in the period
  const { count: referralCount, error: referralError } = await client
    .from('bots')
    .select('*', { count: 'exact', head: true })
    .not('referrer_bot_id', 'is', null)
    .gte('created_at', start.toISOString())
    .lte('created_at', end.toISOString())
    .eq('is_active', true);

  if (referralError) throw referralError;

  // Count deployed (published) bots in the period
  const { count: deployedCount, error: deployedError } = await client
    .from('bots')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'published')
    .gte('created_at', start.toISOString())
    .lte('created_at', end.toISOString())
    .eq('is_active', true);

  if (deployedError) throw deployedError;

  const totalReferrals = referralCount ?? 0;
  const totalDeployed = deployedCount ?? 0;

  // Calculate K-factor
  const kFactor = totalDeployed > 0 ? totalReferrals / totalDeployed : 0;

  // Get unique referring bots count
  const { data: referringBots, error: referringError } = await client
    .from('bots')
    .select('referrer_bot_id')
    .not('referrer_bot_id', 'is', null)
    .gte('created_at', start.toISOString())
    .lte('created_at', end.toISOString())
    .eq('is_active', true);

  if (referringError) throw referringError;

  // Count unique referring bots
  const uniqueReferrers = new Set(referringBots?.map((b) => b.referrer_bot_id));
  const avgReferralsPerBot =
    uniqueReferrers.size > 0 ? totalReferrals / uniqueReferrers.size : 0;

  // Calculate conversion rate from landing page visits
  const { count: visitCount, error: visitError } = await client
    .from('analytics_events')
    .select('*', { count: 'exact', head: true })
    .eq('event_type', 'landing_page_visit')
    .not('referrer_bot_id', 'is', null)
    .gte('created_at', start.toISOString())
    .lte('created_at', end.toISOString());

  if (visitError) throw visitError;

  const visits = visitCount ?? 0;
  const conversionRate = visits > 0 ? totalReferrals / visits : 0;

  return {
    kFactor: Math.round(kFactor * 10000) / 10000, // 4 decimal places
    avgReferralsPerBot: Math.round(avgReferralsPerBot * 100) / 100,
    conversionRate: Math.round(conversionRate * 10000) / 10000,
    totalReferrals,
    totalDeployed,
  };
}

/**
 * Get K-factor trend over time (weekly)
 *
 * Optimized: Uses 2 queries total instead of 2N queries (N = weeks)
 */
export async function getKFactorTrend(
  client: DbClient,
  weeks: number = 12
): Promise<WeeklyKFactor[]> {
  // Calculate date range for all weeks
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - weeks * 7);

  // Fetch all bots in the date range with a single query
  const { data: allBots, error } = await client
    .from('bots')
    .select('created_at, status, referrer_bot_id, is_active')
    .gte('created_at', start.toISOString())
    .lte('created_at', end.toISOString())
    .eq('is_active', true);

  if (error) throw error;

  // Group by week in memory
  const weeklyData = new Map<string, { referrals: number; deployed: number }>();

  // Initialize all weeks
  for (let i = 0; i < weeks; i++) {
    const weekEnd = new Date();
    weekEnd.setDate(weekEnd.getDate() - i * 7);
    const weekStart = new Date(weekEnd);
    weekStart.setDate(weekStart.getDate() - 7);

    const weekNum = getISOWeek(weekStart);
    const year = weekStart.getFullYear();
    const week = `${year}-W${weekNum.toString().padStart(2, '0')}`;

    weeklyData.set(week, { referrals: 0, deployed: 0 });
  }

  // Process all bots and categorize by week
  for (const bot of allBots || []) {
    const createdAt = new Date(bot.created_at);
    const weekNum = getISOWeek(createdAt);
    const year = createdAt.getFullYear();
    const week = `${year}-W${weekNum.toString().padStart(2, '0')}`;

    const data = weeklyData.get(week);
    if (data) {
      if (bot.referrer_bot_id) {
        data.referrals++;
      }
      if (bot.status === 'published') {
        data.deployed++;
      }
    }
  }

  // Convert to results array
  const results: WeeklyKFactor[] = Array.from(weeklyData.entries()).map(
    ([week, data]) => {
      const kFactor = data.deployed > 0 ? data.referrals / data.deployed : 0;
      return {
        week,
        kFactor: Math.round(kFactor * 10000) / 10000,
        referralConversions: data.referrals,
        deployedBots: data.deployed,
      };
    }
  );

  // Sort chronologically (oldest first)
  return results.sort((a, b) => a.week.localeCompare(b.week));
}

/**
 * Get ISO week number for a date
 */
function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/**
 * Get top referring bots by conversion count
 */
export async function getTopReferringBots(
  client: DbClient,
  limit: number = 10,
  dateRange: DateRange = getDefault30DayRange()
): Promise<TopReferringBot[]> {
  const { start, end } = dateRange;

  // Get all bots created via referral in the period
  const { data: referredBots, error } = await client
    .from('bots')
    .select('referrer_bot_id')
    .not('referrer_bot_id', 'is', null)
    .gte('created_at', start.toISOString())
    .lte('created_at', end.toISOString())
    .eq('is_active', true);

  if (error) throw error;

  if (!referredBots || referredBots.length === 0) {
    return [];
  }

  // Count referrals per bot
  const referralCounts = new Map<string, number>();
  for (const bot of referredBots) {
    const refId = bot.referrer_bot_id!;
    referralCounts.set(refId, (referralCounts.get(refId) || 0) + 1);
  }

  // Sort by count and take top N
  const topReferrerIds = Array.from(referralCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([id]) => id);

  if (topReferrerIds.length === 0) {
    return [];
  }

  // Get bot details for top referrers
  const { data: botDetails, error: detailsError } = await client
    .from('bots')
    .select('id, name, slug')
    .in('id', topReferrerIds);

  if (detailsError) throw detailsError;

  // Map to result format
  return topReferrerIds.map((botId) => {
    const bot = botDetails?.find((b) => b.id === botId);
    const count = referralCounts.get(botId) || 0;

    return {
      botId,
      name: bot?.name || 'Unknown Bot',
      slug: bot?.slug || '',
      referralCount: count,
      conversionCount: count, // Same as referralCount for now
    };
  });
}

/**
 * Get referral funnel metrics
 * Tracks: visits → starts → completions → signups
 */
export async function getReferralFunnel(
  client: DbClient,
  dateRange: DateRange = getDefault30DayRange()
): Promise<ReferralFunnel> {
  const { start, end } = dateRange;

  // Helper to count events by type with referrer
  async function countEvents(eventType: string): Promise<number> {
    const { count, error } = await client
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('event_type', eventType)
      .not('referrer_bot_id', 'is', null)
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString());

    if (error) throw error;
    return count ?? 0;
  }

  const [visits, starts, completions, signups] = await Promise.all([
    countEvents('landing_page_visit'),
    countEvents('bot_creation_started'),
    countEvents('bot_creation_completed'),
    countEvents('signup_completed'),
  ]);

  return {
    visits,
    starts,
    completions,
    signups,
  };
}
